import React from 'react';
import type { StartupActivityTimelineItem as StartupActivityTimelineItemType } from '../../types/startupDashboard';
import eyeIcon from '../../assets/eye.png';
import heartIcon from '../../assets/favorites_icon_(green heart).png';
import starIcon from '../../assets/icons/star.png';
import pitchIcon from '../../assets/icons/pitch.png';
import feedbackIcon from '../../assets/feedback.png';
import shareIcon from '../../assets/share_icon2.png';

interface StartupActivityTimelineItemProps {
  item: StartupActivityTimelineItemType;
  showHeader?: boolean;
  mobileStyle?: boolean;
  onSelectInvestor?: () => void;
}

// ── Mobile: colored dot / star bullets ────────────────────
function getDotStyle(item: StartupActivityTimelineItemType): { symbol: string; color: string } {
  const text = (item.text || '').toLowerCase();

  switch (item.eventType) {
    case 'viewed_profile':
    case 'viewed_deck':
      return { symbol: '•', color: '#d1d5db' };
    case 'feedback_left':
      return { symbol: '•', color: '#60a5fa' };
    case 'favorited':
    case 'favorited_with_consent':
      return { symbol: '★', color: '#4ade80' };
    case 'shared':
      return { symbol: '•', color: '#ec4899' };
    default:
      break;
  }

  if (text.includes('not interested')) return { symbol: '•', color: '#ef4444' };
  if (text.includes('view later')) return { symbol: '•', color: '#eab308' };
  if (text.includes('interested')) return { symbol: '★', color: '#4ade80' };
  if (text.includes('share')) return { symbol: '•', color: '#ec4899' };

  return { symbol: '•', color: '#d1d5db' };
}

function extractFeedbackDetail(item: StartupActivityTimelineItemType): string {
  let detail = (item.feedbackDetail || '').trim();
  if (!detail && item.text) {
    if (item.text.includes(':')) {
      detail = item.text.split(':')[1].trim();
    } else if (item.text.includes(' - ')) {
      detail = item.text.split(' - ')[1].trim();
    }
  }
  return detail.replace(/^-+\s*/, '').trim();
}

function getDisplayText(item: StartupActivityTimelineItemType): string {
  if (item.eventType === 'viewed_profile' || item.eventType === 'viewed_deck') {
    return 'Viewed pitch deck';
  }
  if (item.eventType === 'feedback_left' || item.text?.toLowerCase().includes('feedback')) {
    return 'Left feedback';
  }
  if (item.eventType === 'favorited' || item.eventType === 'favorited_with_consent') {
    return 'Is now following your progress';
  }
  if (item.eventType === 'shared' || (item.text || '').toLowerCase().includes('share')) {
    return 'Shared your profile with another investor';
  }
  return (item.text || '').trim();
}

// ── Desktop: original PNG icon helpers ────────────────────
function getDesktopIcon(item: StartupActivityTimelineItemType): string {
  if (item.eventType === 'feedback_left' || item.text?.toLowerCase().includes('feedback')) {
    return feedbackIcon;
  }
  switch (item.eventType) {
    case 'viewed_profile':
    case 'viewed_deck':
      return eyeIcon;
    case 'favorited':
    case 'favorited_with_consent':
      return heartIcon;
    case 'shared':
      return shareIcon;
    default:
      return eyeIcon;
  }
}

function getDesktopText(item: StartupActivityTimelineItemType): React.ReactNode {
  if (item.eventType === 'feedback_left' || item.text?.toLowerCase().includes('feedback')) {
    return 'Left feedback';
  }
  switch (item.eventType) {
    case 'viewed_profile':
    case 'viewed_deck':
      return 'Viewed pitch deck';
    case 'favorited':
    case 'favorited_with_consent':
      return 'Is now following your progress';
    case 'shared':
      return 'Shared your profile with another investor';
    default:
      if (item.text?.toLowerCase().includes('share')) {
        return 'Shared your profile with another investor';
      }
      return item.text;
  }
}

const StartupActivityTimelineItem: React.FC<StartupActivityTimelineItemProps> = ({
  item,
  showHeader = true,
  mobileStyle = false,
  onSelectInvestor,
}) => {
  const isFeedback =
    item.eventType === 'feedback_left' ||
    (item.text && item.text.toLowerCase().includes('feedback'));
  const feedbackDetail = isFeedback ? extractFeedbackDetail(item) : '';

  if (mobileStyle) {
    const { symbol, color } = getDotStyle(item);
    const displayText = getDisplayText(item);
    return (
      <div className="min-w-0">
        {showHeader && (
          <h4 className="mb-1 text-sm italic text-gray-300">
            {item.investorName || `Investor from ${item.country}`}
          </h4>
        )}
        <div className="flex items-start gap-1.5">
          <span className="mt-px flex-shrink-0 text-sm leading-snug" style={{ color }}>
            {symbol}
          </span>
          <div>
            <p className="text-sm leading-snug text-gray-300">{displayText}</p>
            {isFeedback && feedbackDetail && (
              <p className="mt-0 text-sm italic leading-snug text-gray-400">- {feedbackDetail}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop (original) ────────────────────────────────────
  if (item.eventType === 'favorited_with_consent') {
    return (
      <div className="min-w-0">
        {showHeader && (
          <h4 className="mb-1 text-sm font-semibold text-white">
            {item.investorName || `Investor from ${item.country}`}
          </h4>
        )}
        <div className="mt-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <img
              src={heartIcon}
              alt=""
              className="h-[18px] w-[18px] shrink-0 object-contain"
              aria-hidden
            />
            <p className="text-sm text-gray-300">Is now following your progress</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#f59e0b] text-base leading-none w-[18px] text-center shrink-0 select-none">
              ★
            </span>
            <p className="text-sm text-gray-300">
              <span className="underline">and</span> agreed to share their contact info with you
            </p>
          </div>
          <div className="pl-6">
            <p className="text-[10px] leading-snug text-gray-400 sm:text-xs">
              [click on the &apos;Interested&apos; tab to view them]
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (item.eventType === 'shared' || item.text?.toLowerCase().includes('share')) {
    return (
      <div className="min-w-0">
        {showHeader && (
          <h4 className="mb-1 text-sm font-semibold text-white">
            {item.investorName || `Investor from ${item.country}`}
          </h4>
        )}
        <div className="flex items-center gap-2">
          <img
            src={shareIcon}
            alt=""
            className="h-[18px] w-[18px] shrink-0 object-contain"
            aria-hidden
          />
          <p className="text-sm leading-snug text-gray-300">
            Shared your profile with another investor
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {showHeader && (
        <h4 className="mb-1 text-sm font-semibold text-white">
          {item.investorName || `Investor from ${item.country}`}
        </h4>
      )}
      <div className="mt-1 flex flex-col gap-0">
        <div
          className={`flex items-center gap-2 ${onSelectInvestor ? 'cursor-pointer group' : ''}`}
          onClick={onSelectInvestor}
          role={onSelectInvestor ? 'button' : undefined}
          tabIndex={onSelectInvestor ? 0 : undefined}
        >
          <img
            src={getDesktopIcon(item)}
            alt=""
            className="h-[18px] w-[18px] shrink-0 object-contain group-hover:opacity-90 transition-opacity"
            aria-hidden
          />
          <p className="text-sm leading-snug text-gray-300 group-hover:text-white transition-colors">
            {getDesktopText(item)}
          </p>
        </div>
        {isFeedback && feedbackDetail && (
          <div className="pl-6">
            <p className="text-sm font-normal italic leading-snug text-gray-400">
              - {feedbackDetail}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartupActivityTimelineItem;
